import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import LocationCascade from "@/components/LocationCascade";
import TranslatableInput from "@/components/TranslatableInput";
import { Link2, Loader2, X, Plus, Trash2, ExternalLink, Search, ShieldCheck, Zap } from "lucide-react";

const DESCRIPTION_EXAMPLES = [
  "Loving husband, father, and grandfather.",
  "Former teacher at Lincoln High School.",
  "Veteran of the U.S. Army.",
  "Long-time resident of Portland.",
];

const border = { borderColor: "var(--wofy-border)" };
const inputCls = "w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent";

export default function CelebrateLinkExisting() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const [form, setForm] = useState({
    // Basic
    full_name: sp.get("name") || "",
    preferred_name: "",
    family_name: "",
    birth_date: "",
    age_at_death: "",
    death_date: "",
    city: "",
    state: "",
    country: "United States",
    // Memorial (i18n-capable → string OR {__base, en, ...})
    title: "",
    description: "",
    description_choice: "",
    // Link
    external_url: sp.get("url") || "",
    // Family
    spouse: "",
    children: "",   // comma-separated
    parents: "",
    family_tree_slug: "",
    // Media
    photo: "",
    // Categorisation
    categories: ["obituary"],
    tags: "",
    note: "",
    // Submitter
    relationship: "relative",
    // Permissions
    perm_accurate: false,
    perm_permission: false,
    perm_understands_link_only: false,
  });

  const [location, setLocation] = useState({
    country: "United States", state: "", county: "", city: "", zip: "",
  });

  // Admin fast-track mode toggle. Available only to admins.
  const isAdmin = !!user?.is_admin;
  const [adminMode, setAdminMode] = useState(isAdmin);

  useEffect(() => {
    http.get("/memorial-links/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleCategory = (c) => {
    setForm((f) => {
      const has = f.categories.includes(c);
      return { ...f, categories: has ? f.categories.filter((x) => x !== c) : [...f.categories, c] };
    });
  };

  const runPreview = async () => {
    if (!form.external_url.trim()) return toast.error("Paste the obituary URL first");
    setPreviewing(true);
    try {
      const { data } = await http.post("/memorial-links/preview", { url: form.external_url.trim() });
      setPreview(data);
      if (data.page_title && !form.title) set("title", data.page_title);
      if (data.excerpt && !form.description) set("description", data.excerpt);
      if (data.featured_image && !form.photo) set("photo", data.featured_image);
      toast.success("Preview loaded — review before submitting");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch preview");
    } finally {
      setPreviewing(false);
    }
  };

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Photo must be under 3MB");
    const r = new FileReader();
    r.onload = () => set("photo", r.result);
    r.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in to link an obituary."); nav("/sign-in?next=/celebrate/link-existing"); return; }
    if (!form.external_url.trim()) { toast.error("External URL is required."); return; }

    // ---- Admin fast-track path ----
    if (isAdmin && adminMode) {
      if (!form.full_name.trim()) { toast.error("Full name is required."); return; }
      const adminPayload = {
        external_url: form.external_url.trim(),
        full_name: form.full_name.trim(),
        death_date: form.death_date || undefined,
        country: location.country || form.country || undefined,
        title: form.title || undefined,
        description: form.description || undefined,
        photo: form.photo || undefined,
        categories: form.categories,
        tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
        note: form.note || undefined,
        family_name: form.family_name || undefined,
      };
      setBusy(true);
      try {
        const { data } = await http.post("/admin/memorial-links/quick", adminPayload);
        toast.success("Admin fast-track — link is live.");
        nav(`/celebrate/link/${data.slug}`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Admin submission failed");
      } finally {
        setBusy(false);
      }
      return;
    }

    // ---- Regular user path (full validation) ----
    if (!form.perm_accurate || !form.perm_permission || !form.perm_understands_link_only) {
      toast.error("Please accept all three permission checkboxes.");
      return;
    }
    // TranslatableInput may hold either a plain string or i18n dict.
    const isEmpty = (v) => !v || (typeof v === "string" && !v.trim()) || (typeof v === "object" && !Object.entries(v).some(([k, val]) => k !== "__base" && (val || "").trim().length > 0));
    if (!form.full_name.trim() || !form.family_name.trim() || isEmpty(form.title) || isEmpty(form.description) || !form.external_url.trim() || !form.death_date) {
      toast.error("Please complete every required field marked with *");
      return;
    }
    if (!form.birth_date && !form.age_at_death) {
      toast.error("Please provide either a birth date OR an approximate age at death.");
      return;
    }

    const payload = {
      full_name: form.full_name.trim(),
      preferred_name: form.preferred_name || undefined,
      family_name: form.family_name.trim(),
      birth_date: form.birth_date || undefined,
      age_at_death: form.age_at_death ? Number(form.age_at_death) : undefined,
      death_date: form.death_date,
      city: location.city || form.city || undefined,
      state: location.state || form.state || undefined,
      country: location.country || form.country,
      title: form.title,
      description: form.description,
      external_url: form.external_url.trim(),
      preview: preview || undefined,
      spouse: form.spouse || undefined,
      children: form.children ? form.children.split(",").map((s) => s.trim()).filter(Boolean) : [],
      parents: form.parents ? form.parents.split(",").map((s) => s.trim()).filter(Boolean) : [],
      family_tree_slug: form.family_tree_slug || undefined,
      photo: form.photo || undefined,
      categories: form.categories,
      tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
      note: form.note || undefined,
      relationship: form.relationship,
      perm_accurate: true,
      perm_permission: true,
      perm_understands_link_only: true,
    };
    setBusy(true);
    try {
      const { data } = await http.post("/memorial-links", payload);
      if (data.duplicate_status && data.duplicate_status.startsWith("duplicate_of:")) {
        toast.info("Similar link already exists — yours is queued for admin review.");
      } else {
        toast.success("Thank you — your submission is awaiting review.");
      }
      nav(`/celebrate/link/${data.slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="link-signin-gate">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>CELEBRATE A LIFE · LINK</div>
        <h1 className="font-display text-4xl mb-3">Sign in to link an obituary</h1>
        <p className="text-sm mb-6" style={{ color: "var(--wofy-text-soft)" }}>
          Linking to an external memorial requires a registered Social Pulse account so we can protect against duplicates and moderate responsibly.
        </p>
        <Link to="/sign-in?next=/celebrate/link-existing" className="wofy-btn-primary inline-flex">Sign in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="celebrate-link-page">
      <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>CELEBRATE A LIFE · LINK</div>
      <h1 className="font-display text-4xl md:text-5xl">Link an existing obituary or memorial</h1>
      <p className="mt-4 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
        Point Social Pulse to an obituary already published elsewhere — a newspaper, funeral home, or family site. We'll preserve a small preview card and drive readers to the source.
      </p>

      {isAdmin && (
        <div className="mt-6 rounded-xl border p-3 flex items-center justify-between gap-3" style={border} data-testid="link-admin-mode-row">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            <div>
              <div className="text-sm font-medium">Admin fast-track</div>
              <div className="text-[11px] opacity-70">Only URL + full name are required. Everything else auto-populates from the preview. Auto-approved & marked <em>administrator_verified</em>.</div>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={adminMode} onChange={(e) => setAdminMode(e.target.checked)} data-testid="link-admin-mode-toggle" />
            <span>Fast-track</span>
          </label>
        </div>
      )}

      <form onSubmit={submit} className="mt-10 space-y-6">
        {/* External link + preview */}
        <SectionCard title="External memorial link *">
          <div className="grid md:grid-cols-[1fr_auto] gap-2">
            <input
              type="url"
              placeholder="https://example.com/obituaries/john-smith"
              value={form.external_url}
              onChange={(e) => set("external_url", e.target.value)}
              className={inputCls}
              style={border}
              data-testid="link-external-url"
              required
            />
            <button type="button" onClick={runPreview} disabled={previewing || !form.external_url} className="wofy-btn-ghost" data-testid="link-preview-btn">
              {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {previewing ? "Fetching…" : "Fetch preview"}
            </button>
          </div>
          {preview && (
            <div className="mt-3 rounded-xl border p-3 flex gap-3" style={border}>
              {preview.featured_image ? (
                <img src={preview.featured_image} alt="" className="w-24 h-24 object-cover rounded" />
              ) : (
                <div className="w-24 h-24 rounded bg-black/5 flex items-center justify-center text-[10px] font-mono">No image</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{preview.page_title || "(no title)"}</div>
                <div className="text-[11px] font-mono opacity-70 truncate">{preview.site_name || preview.domain}</div>
                {preview.excerpt && <div className="text-[12px] mt-1 line-clamp-2 opacity-80">{preview.excerpt}</div>}
              </div>
            </div>
          )}
          <p className="text-[11px] opacity-70 mt-2">The system can pull the page title, image, site name and excerpt automatically — you can edit them below before submitting.</p>
        </SectionCard>

        {/* Basic info */}
        {!adminMode ? (
          <SectionCard title="Basic information">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Full name of the deceased *" required value={form.full_name} onChange={(v) => set("full_name", v)} testid="link-full-name" />
              <Field label="Preferred name" value={form.preferred_name} onChange={(v) => set("preferred_name", v)} testid="link-preferred" />
              <Field label="Family name *" required value={form.family_name} onChange={(v) => set("family_name", v)} testid="link-family-name" />
              <Field type="date" label="Date of birth" value={form.birth_date} onChange={(v) => set("birth_date", v)} testid="link-birth" />
              {!form.birth_date && (
                <Field type="number" label="Approximate age at death" value={form.age_at_death} onChange={(v) => set("age_at_death", v)} testid="link-age" />
              )}
              <Field type="date" label="Date of death *" required value={form.death_date} onChange={(v) => set("death_date", v)} testid="link-death" />
            </div>
            <div className="mt-3">
              <div className="text-[11px] font-mono uppercase tracking-wider mb-2">Location</div>
              <LocationCascade value={location} onChange={setLocation} testidPrefix="link-loc" />
            </div>
          </SectionCard>
        ) : (
          <SectionCard title="Basic (admin fast-track)">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Full name of the deceased *" required value={form.full_name} onChange={(v) => set("full_name", v)} testid="link-full-name" />
              <Field type="date" label="Date of death (optional)" value={form.death_date} onChange={(v) => set("death_date", v)} testid="link-death" />
              <Field label="Family name (optional)" value={form.family_name} onChange={(v) => set("family_name", v)} testid="link-family-name" />
              <Field label="Country (optional)" value={location.country} onChange={(v) => setLocation({ ...location, country: v })} />
            </div>
          </SectionCard>
        )}

        {/* Memorial info */}
        <SectionCard title="Memorial information">
          <TranslatableInput
            label="Memorial / obituary title *"
            value={form.title}
            onChange={(v) => set("title", v)}
            testid="link-title"
            required={!adminMode}
          />
          <div>
            <TranslatableInput
              label="Brief description *"
              as="textarea"
              rows={4}
              value={form.description}
              onChange={(v) => set("description", v)}
              testid="link-description"
              required={!adminMode}
            />
            <div className="mt-2 flex flex-wrap gap-1">
              <span className="text-[11px] opacity-70 mr-1">Examples:</span>
              {DESCRIPTION_EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => {
                    const current = typeof form.description === "string" ? form.description : "";
                    set("description", current ? `${current}\n${ex}` : ex);
                  }}
                  className="text-[11px] px-2 py-1 rounded-full border hover:bg-black/5"
                  style={border}
                  data-testid={`link-desc-example-${ex.slice(0, 12)}`}
                >
                  + {ex}
                </button>
              ))}
            </div>
          </div>
        </SectionCard>

        {/* Family */}
        {!adminMode && (
        <SectionCard title="Family (optional)">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Spouse" value={form.spouse} onChange={(v) => set("spouse", v)} />
            <Field label="Children (comma separated)" value={form.children} onChange={(v) => set("children", v)} />
            <Field label="Parents (comma separated)" value={form.parents} onChange={(v) => set("parents", v)} />
            <Field label="Link to family tree (slug)" value={form.family_tree_slug} onChange={(v) => set("family_tree_slug", v)} testid="link-family-tree" />
          </div>
        </SectionCard>
        )}

        {/* Photo */}
        <SectionCard title="Photo (optional)">
          <div className="flex items-start gap-3">
            {form.photo ? (
              <div className="relative">
                <img src={form.photo} alt="" className="h-24 w-24 object-cover rounded-lg" />
                <button type="button" onClick={() => set("photo", "")} className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <div className="h-24 w-24 rounded-lg bg-black/5 flex items-center justify-center text-[10px] font-mono">No photo</div>
            )}
            <div className="flex-1 space-y-2">
              <label className="wofy-btn-ghost cursor-pointer inline-flex" data-testid="link-photo-upload">
                Upload photo
                <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
              </label>
              {preview?.featured_image && (
                <button type="button" onClick={() => set("photo", preview.featured_image)} className="wofy-btn-ghost text-xs">Use preview image</button>
              )}
              <p className="text-[11px] opacity-70">Or leave blank — we'll fall back to the external site's preview image.</p>
            </div>
          </div>
        </SectionCard>

        {/* Categories */}
        <SectionCard title="Categories">
          <div className="flex flex-wrap gap-2">
            {(cfg?.categories || []).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => toggleCategory(c)}
                className={`text-xs px-3 py-1.5 rounded-full border ${form.categories.includes(c) ? "bg-black text-white border-black" : "hover:bg-black/5"}`}
                style={!form.categories.includes(c) ? border : undefined}
                data-testid={`link-cat-${c}`}
              >
                {c.replace(/_/g, " ")}
              </button>
            ))}
          </div>
          <div className="mt-3">
            <Field label="Tags (comma separated)" placeholder="veteran, educator, clergy" value={form.tags} onChange={(v) => set("tags", v)} testid="link-tags" />
          </div>
        </SectionCard>

        {/* Notes */}
        <SectionCard title="Notes (optional)">
          <TranslatableInput
            label="Note"
            as="textarea"
            rows={3}
            value={form.note}
            onChange={(v) => set("note", v)}
            placeholder='e.g. "I am a family member." · "Published by the local newspaper."'
            testid="link-note"
          />
        </SectionCard>

        {/* Submitted by */}
        {!adminMode && (
        <SectionCard title="Submitted by">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Full name *" value={user.name} onChange={() => {}} disabled />
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider">Relationship to deceased</label>
              <select value={form.relationship} onChange={(e) => set("relationship", e.target.value)} className={inputCls} style={border} data-testid="link-relationship">
                {(cfg?.relationships || []).map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
              </select>
            </div>
          </div>
        </SectionCard>
        )}

        {/* Permissions */}
        {!adminMode && (
        <SectionCard title="Permissions">
          <label className="flex items-start gap-2 text-sm mb-2">
            <input type="checkbox" checked={form.perm_accurate} onChange={(e) => set("perm_accurate", e.target.checked)} data-testid="link-perm-1" />
            <span>I believe this information is accurate.</span>
          </label>
          <label className="flex items-start gap-2 text-sm mb-2">
            <input type="checkbox" checked={form.perm_permission} onChange={(e) => set("perm_permission", e.target.checked)} data-testid="link-perm-2" />
            <span>I have permission to submit this information, or it is already publicly available.</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={form.perm_understands_link_only} onChange={(e) => set("perm_understands_link_only", e.target.checked)} data-testid="link-perm-3" />
            <span>I understand Social Pulse links to external memorial pages and does not claim ownership of their content.</span>
          </label>
        </SectionCard>
        )}

        <div className="flex justify-end">
          <button disabled={busy} className="wofy-btn-primary" data-testid="link-submit">
            {busy ? "Submitting…" : (<><Link2 className="w-4 h-4" /> {adminMode ? "Fast-track submit" : "Submit link"}</>)}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------- primitives ---------- */
function SectionCard({ title, children }) {
  return (
    <div className="wofy-card space-y-3">
      <div className="text-[11px] font-mono uppercase tracking-wider">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Field({ label, value, onChange, type = "text", required, placeholder, testid, disabled }) {
  return (
    <label className="block">
      <span className="text-[11px] font-mono uppercase tracking-wider">{label}</span>
      <input
        required={required}
        type={type}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(e) => onChange && onChange(e.target.value)}
        className={inputCls}
        style={border}
        disabled={disabled}
        data-testid={testid}
      />
    </label>
  );
}
